import React, { forwardRef } from 'react'; // Import forwardRef

/**
 * Button component with multiple variants and sizes
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Button content
 * @param {('primary'|'secondary'|'outline'|'text')} [props.variant='primary'] - Button style variant
 * @param {('sm'|'md'|'lg')} [props.size='md'] - Button size
 * @param {boolean} [props.fullWidth=false] - Whether button should take full width
 * @param {React.ReactNode} [props.leftIcon] - Icon to display on the left
 * @param {React.ReactNode} [props.rightIcon] - Icon to display on the right
 * @param {boolean} [props.isLoading=false] - Whether button is in loading state
 * @param {string} [props.className] - Additional CSS classes
 * @param {React.Ref} ref - Ref forwarded to the button element
 */
const Button = forwardRef(
  (
    {
      children,
      variant = 'primary',
      size = 'sm',
      fullWidth = false,
      leftIcon,
      rightIcon,
      isLoading = false,
      className = '',
      ...props
    },
    ref,
  ) => {
    const baseStyles =
      'inline-flex items-center justify-center rounded-md font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-0 focus:ring-primary focus:border-primary rounded-lg cursor-pointer';

    const variants = {
      primary: 'primary-background white-text hover:primary-background border-color',
      secondary: 'bg-gray-200 text-gray-900 hover:bg-gray-300 focus:ring-gray-500',
      danger: 'bg-red-600 white-text hover:bg-red-700 focus:ring-red-500',
      outline: 'border border-gray-300 bg-transparent text-gray-700 hover:bg-gray-50',
    };

    const sizes = {
      sm: 'px-3 py-1.5 text-sm',
      md: 'px-3 py-1.5 text-base',
      lg: 'px-6 py-3 text-lg',
    };

    return (
      <button
        ref={ref}
        className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${className} flex items-center gap-x-2`}
        {...props}
      >
        {/* {children} */}
        {leftIcon && <span className="mr-2">{leftIcon}</span>}
        {isLoading ? 'Loading...' : children}
        {rightIcon && <span className="ml-2">{rightIcon}</span>}
      </button>
    );
  },
);

export default Button;
